export default function Documentation() {
  return (
    <section id="documentation" className="relative py-32 overflow-hidden">
      <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(139,92,246,0.03) 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#8b5cf6]/10 border border-[#8b5cf6]/30 px-4 py-2 rounded-full mb-6">
            <i className="ri-book-open-line text-[#8b5cf6]"></i>
            <span className="text-[#8b5cf6] text-sm font-medium">Documentation</span>
          </div>
          <h2 className="text-5xl font-bold text-white mb-6 font-['Space_Grotesk']">
            Get Started in Minutes
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Comprehensive documentation to help you integrate Bellibutton into your workflow
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          <div className="lg:col-span-2 bg-gradient-to-br from-[#8b5cf6]/10 to-transparent border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-gray-500 text-sm ml-2 font-mono">bellibutton.config.js</span>
            </div>
            <pre className="text-sm font-mono overflow-x-auto">
              <code className="text-gray-300">
                <span className="text-[#8b5cf6]">module.exports</span> = {'{'}{'\n'}
                {'  '}<span className="text-[#00d9ff]">rules</span>: {'{'}{'\n'}
                {'    '}<span className="text-gray-500">// Indentation rules</span>{'\n'}
                {'    '}<span className="text-white">indent</span>: {'{'}{'\n'}
                {'      '}<span className="text-[#00d9ff]">type</span>: <span className="text-yellow-400">'spaces'</span>,{'\n'}
                {'      '}<span className="text-[#00d9ff]">size</span>: <span className="text-yellow-400">2</span>{'\n'}
                {'    '}{'}'},{'\n\n'}
                {'    '}<span className="text-gray-500">// Quote style</span>{'\n'}
                {'    '}<span className="text-white">quotes</span>: {'{'}{'\n'}
                {'      '}<span className="text-[#00d9ff]">style</span>: <span className="text-yellow-400">'single'</span>,{'\n'}
                {'      '}<span className="text-[#00d9ff]">avoidEscape</span>: <span className="text-yellow-400">true</span>{'\n'}
                {'    '}{'}'},{'\n\n'}
                {'    '}<span className="text-gray-500">// Semicolons</span>{'\n'}
                {'    '}<span className="text-white">semicolons</span>: {'{'}{'\n'}
                {'      '}<span className="text-[#00d9ff]">required</span>: <span className="text-yellow-400">true</span>,{'\n'}
                {'      '}<span className="text-[#00d9ff]">omitLastInBlock</span>: <span className="text-yellow-400">false</span>{'\n'}
                {'    '}{'}'},{'\n\n'}
                {'    '}<span className="text-gray-500">// Line length</span>{'\n'}
                {'    '}<span className="text-white">maxLineLength</span>: <span className="text-yellow-400">100</span>{'\n'}
                {'  '}{'}'},{'\n\n'}
                {'  '}<span className="text-[#00d9ff]">export</span>: [<span className="text-yellow-400">'eslint'</span>, <span className="text-yellow-400">'pylint'</span>, <span className="text-yellow-400">'rustfmt'</span>]{'\n'}
                {'}'};
              </code>
            </pre>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-2xl p-6">
              <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                <i className="ri-terminal-line text-[#00d9ff]"></i>
                Quick Install
              </h4>
              <div className="bg-[#0a0a0a] rounded-lg p-4 font-mono text-sm">
                <div className="text-gray-500 mb-2"># npm</div>
                <div className="text-[#00d9ff]">npm install -g bellibutton</div>
                <div className="text-gray-500 mt-4 mb-2"># yarn</div>
                <div className="text-[#00d9ff]">yarn global add bellibutton</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-2xl p-6">
              <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                <i className="ri-file-code-line text-[#8b5cf6]"></i>
                Export Rules
              </h4>
              <div className="bg-[#0a0a0a] rounded-lg p-4 font-mono text-sm">
                <div className="text-[#00d9ff]">bellibutton export --to eslint</div>
                <div className="text-gray-500 mt-2">✓ Generated .eslintrc.json</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <a
            href="#"
            className="group bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-2xl p-8 hover:border-[#00d9ff]/50 transition-all duration-300 cursor-pointer"
          >
            <div className="w-12 h-12 bg-[#00d9ff]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#00d9ff]/20 transition-colors">
              <i className="ri-book-2-line text-[#00d9ff] text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Full Documentation</h3>
            <p className="text-gray-400 mb-4">Complete guides, API references, and examples</p>
            <div className="flex items-center gap-2 text-[#00d9ff] group-hover:gap-3 transition-all">
              <span className="text-sm font-medium">Read Docs</span>
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>

          <a
            href="#"
            className="group bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-2xl p-8 hover:border-[#8b5cf6]/50 transition-all duration-300 cursor-pointer"
          >
            <div className="w-12 h-12 bg-[#8b5cf6]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#8b5cf6]/20 transition-colors">
              <i className="ri-code-box-line text-[#8b5cf6] text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">API Reference</h3>
            <p className="text-gray-400 mb-4">Detailed API documentation for developers</p>
            <div className="flex items-center gap-2 text-[#8b5cf6] group-hover:gap-3 transition-all">
              <span className="text-sm font-medium">View API</span>
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>

          <a
            href="#"
            className="group bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-2xl p-8 hover:border-[#00d9ff]/50 transition-all duration-300 cursor-pointer"
          >
            <div className="w-12 h-12 bg-[#00d9ff]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#00d9ff]/20 transition-colors">
              <i className="ri-question-answer-line text-[#00d9ff] text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Community Support</h3>
            <p className="text-gray-400 mb-4">Get help from our active community</p>
            <div className="flex items-center gap-2 text-[#00d9ff] group-hover:gap-3 transition-all">
              <span className="text-sm font-medium">Join Discord</span>
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
